import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowUpRight } from "lucide-react";

const services = [
  {
    id: "gestion",
    num: "01",
    icon: "/assets/icono01.png",
    title: "Gestión de Residuos",
    subtitle: "Sólidos y líquidos",
    bgPhoto: "/assets/ops1.jpg",
    items: [
      {
        t: "Residuos No Especiales y Reciclables",
        d: "Logística integral para residuos industriales, comerciales y orgánicos. Enfoque en economía circular para valorizar sus descartes.",
      },
      {
        t: "Tratamiento de Residuos Especiales y Peligrosos",
        d: "Recolección, transporte y tratamiento de residuos especiales y peligrosos para corrientes de desecho críticas. Inertización, tratamiento físico-químico, rellenos de seguridad y certificado de disposición final.",
      },
      {
        t: "Gestión de Efluentes",
        d: "Succión, transporte y tratamiento de efluentes líquidos y semisólidos en plantas habilitadas.",
      },
    ],
  },
  {
    id: "rolloff",
    num: "02",
    icon: "/assets/icono03.png",
    title: "Transporte Roll Off y Atmosféricos",
    subtitle: "Roll Off en Buenos Aires",
    bgPhoto: "/assets/ops3.jpg",
    items: [
      {
        t: "Transporte Roll Off en Buenos Aires",
        d: "Servicio de transporte roll off en Buenos Aires (CABA y toda la Provincia de Buenos Aires) para grandes volúmenes de residuos industriales, escombros y descartes. Provisión, colocación y retiro de contenedores roll off (volquetes) en obra, planta o depósito.",
      },
      {
        t: "Contenedores Roll Off (Volquetes)",
        d: "Cajas roll off de distintas capacidades para acopio y traslado eficiente de residuos sólidos en Buenos Aires. Logística a medida con retiro programado y trazabilidad documentada.",
      },
      {
        t: "Servicios Atmosféricos",
        d: "Camiones atmosféricos para succión, transporte y disposición de líquidos, barros, efluentes y limpieza de pozos, cámaras y plantas de tratamiento.",
      },
    ],
  },
  {
    id: "saneamiento",
    num: "03",
    icon: "/assets/icono03.png",
    title: "Saneamiento e Infraestructura",
    subtitle: "Industria y servicios",
    items: [
      {
        t: "Limpieza Técnica de Plantas",
        d: "Intervención profunda en naves industriales y depósitos para garantizar seguridad e higiene laboral.",
      },
      {
        t: "Desobstrucción Hidrocinética",
        d: "Mantenimiento de redes cloacales y pluviales con equipos de alta presión.",
      },
      {
        t: "Destrucción Certificada de Activos",
        d: "Decomisos y destrucción de mercadería (vencida o fallada) con protección de marca.",
      },
    ],
  },
  {
    id: "consultoria",
    num: "04",
    icon: "/assets/icono04.png",
    title: "Consultoría Técnica y Legal",
    subtitle: "Asesoramiento integral",
    items: [
      {
        t: "Asesoramiento en Origen",
        d: "Optimizamos sus procesos internos para reducir costos y mejorar procesos.",
      },
      {
        t: "Reordenamiento Técnico-Legal",
        d: "Clasificación de corrientes de desecho y gestión de inscripciones ante organismos gubernamentales.",
      },
      {
        t: "Evaluación de Impacto",
        d: "Soporte técnico para asegurar que su empresa cumpla con las expectativas y resoluciones vigentes.",
      },
    ],
  },
  {
    id: "cumplimiento",
    num: "05",
    icon: "/assets/icono05.png",
    title: "Cumplimiento Normativo",
    subtitle: "Garantizado",
    bgPhoto: "/assets/ops2.jpg",
    items: [
      {
        t: "Habilitaciones",
        d: "Operamos bajo estándares del Ministerio de Ambiente de la Provincia de Buenos Aires (ex-OPDS).",
      },
      {
        t: "Documentación Crítica",
        d: "Gestión de Manifiestos Electrónicos y entrega de Certificado de Disposición Final.",
      },
      {
        t: "Trazabilidad Total",
        d: "Control riguroso desde la carga en su planta hasta el destino sustentable.",
      },
    ],
  },
];

function ServiceBlock({ svc, index, even, wide }) {
  const [open, setOpen] = useState(0);
  const hasPhoto = !!svc.bgPhoto;

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
      data-testid={`service-card-${svc.id}`}
      className={`relative border-t-2 border-asphalt ${
        wide ? "lg:col-span-2" : even ? "lg:border-l-2" : ""
      }`}
    >
      <div className={`relative h-full ${hasPhoto ? "bg-asphalt text-bone" : "bg-bone text-asphalt"}`}>
        {hasPhoto && (
          <div className="absolute inset-0 z-0">
            <img
              src={svc.bgPhoto}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-asphalt via-asphalt/85 to-asphalt/60" />
          </div>
        )}
        <div
          className={`relative z-10 p-8 md:p-12 lg:p-14 min-h-[560px] h-full ${
            wide
              ? "lg:grid lg:grid-cols-2 lg:gap-14 lg:items-center"
              : "flex flex-col"
          }`}
        >
          <div
            className={`flex items-start justify-between gap-6 ${
              wide ? "mb-10 lg:mb-0" : "mb-10"
            }`}
          >
            <div>
              <div
                className={`tag mb-4 ${
                  hasPhoto ? "text-lime-400" : "text-forest-600"
                }`}
              >
                {svc.num} — {svc.subtitle}
              </div>
              <h3 className="font-display text-4xl md:text-5xl lg:text-[56px] leading-[0.92] max-w-md">
                {svc.title}
              </h3>
            </div>
            <div
              className={`shrink-0 w-20 h-20 inline-flex items-center justify-center border-2 ${
                hasPhoto ? "border-lime-400/40 bg-asphalt/30" : "border-asphalt bg-bone"
              }`}
            >
              <img
                src={svc.icon}
                alt=""
                aria-hidden="true"
                className={`w-12 h-12 object-contain ${
                  hasPhoto ? "" : "invert-0"
                }`}
                style={hasPhoto ? { filter: "brightness(0) invert(1)" } : {}}
              />
            </div>
          </div>

          <div className={`${wide ? "" : "mt-auto"} space-y-px`}>
            {svc.items.map((item, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={i}
                  className={`border-t ${
                    hasPhoto ? "border-bone/15" : "border-asphalt/15"
                  }`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`service-toggle-${svc.id}-${i}`}
                    className="w-full text-left py-5 flex items-center justify-between gap-4 group"
                    aria-expanded={isOpen}
                  >
                    <span
                      className={`font-display text-xl md:text-2xl ${
                        hasPhoto ? "text-bone" : "text-asphalt"
                      } group-hover:text-lime-500 transition-colors`}
                    >
                      {item.t}
                    </span>
                    <span
                      className={`shrink-0 w-8 h-8 inline-flex items-center justify-center border ${
                        hasPhoto
                          ? "border-bone/30 text-bone group-hover:bg-lime-400 group-hover:text-asphalt group-hover:border-lime-400"
                          : "border-asphalt/40 text-asphalt group-hover:bg-asphalt group-hover:text-bone"
                      } transition-colors`}
                    >
                      {isOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <p
                          className={`pb-6 pr-12 text-sm leading-relaxed ${
                            hasPhoto ? "text-bone/75" : "text-forest-600"
                          }`}
                        >
                          {item.d}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="bg-bone relative"
    >
      {/* Section intro */}
      <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="tag text-forest-600 mb-5 lime-rule">
              Servicios
            </div>
            <h2 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[80px] text-asphalt leading-[0.92]">
              Servicios integrales
              <br />
              <span className="text-forest-700">de gestión ambiental.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <p className="text-forest-600 leading-relaxed text-lg max-w-md">
              De la planta industrial al hogar: cubrimos toda la cadena de
              valor con habilitaciones vigentes, equipo técnico propio y
              documentación de respaldo en cada operación.
            </p>
            <a
              href="#contacto"
              className="mt-6 inline-flex items-center gap-2 font-display text-xl text-asphalt hover:text-lime-600 transition-colors"
            >
              Cotizá tu operación
              <ArrowUpRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      {/* 2x2 grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 border-b-2 border-asphalt">
        {services.map((s, i) => {
          const isOddLast =
            services.length % 2 === 1 && i === services.length - 1;
          return (
            <ServiceBlock
              key={s.id}
              svc={s}
              index={i}
              even={i % 2 === 1}
              wide={isOddLast}
            />
          );
        })}
      </div>
    </section>
  );
}
